import { useReportProductManifestProps } from '@euler/app/flows/report/functions/useReportProductManifestProps';
import { ProductManifestProps } from '@euler/app/flows/report/types';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import { TaskProductManifestItemOrigin } from '@euler/model/enum';
import { VehicleReport } from '@euler/model/report';
import { memo, useCallback } from 'react';
import { ScrollViewProps } from 'react-native';
import { ReportContentWrapper } from '../components/ReportContent';
import {
  ReportScreenProps,
  useReportContext,
} from '../components/ReportContext';
import { InspectionReportView } from '../components/inspection/InspectionReportView';
import { InspectionReportViewForIpad } from '../components/inspection/InspectionReportView.iPad';

type Props = {
  flowId?: string;
  productManifest?: ProductManifestProps;
  faultLikeSubject?: FaultInspectionItem;
};

const Content = memo(
  ({
    flowId,
    productManifest,
    faultLikeSubject,
    ...scrollViewProps
  }: ScrollViewProps & Props) => {
    const { report } = useReportContext();
    const isMobile = useIsMobileLayout();

    if (!isMobile) {
      return (
        <InspectionReportViewForIpad
          report={report}
          flowId={flowId}
          productManifest={productManifest}
          faultLikeSubject={faultLikeSubject}
        />
      );
    }

    return (
      <InspectionReportView
        report={report}
        flowId={flowId}
        productManifest={productManifest}
        faultLikeSubject={faultLikeSubject}
        {...scrollViewProps}
      />
    );
  },
);

export const InspectionReportScreen = wrapNavigatorScreen(
  ({
    title,
    flowId,
    faultLikeSubject,
    productManifest: _productManifest,
    ...props
  }: ReportScreenProps & Props) => {
    const productManifest = useReportProductManifestProps({
      productManifest: _productManifest,
      origin: { type: TaskProductManifestItemOrigin.InspectionPreview },
    });

    const isMobile = useIsMobileLayout();

    const renderScrollView = useCallback(
      (scrollViewProps: ScrollViewProps) => {
        return (
          <Content
            flowId={flowId}
            productManifest={productManifest}
            faultLikeSubject={faultLikeSubject}
            {...scrollViewProps}
          />
        );
      },
      [faultLikeSubject, flowId, productManifest],
    );

    const subTitle = useCallback(
      (report: VehicleReport) => {
        const flow = report.inspection?.flows?.find(x => x.id === flowId);
        return flow?.name;
      },
      [flowId],
    );

    return (
      <ReportContentWrapper
        tab="inspection"
        title={title ?? '检测报告'}
        subTitle={subTitle}
        {...props}
        renderScrollView={renderScrollView}
        shouldUseLandscapeLayout={!isMobile}
      >
        <Content
          flowId={flowId}
          productManifest={productManifest}
          faultLikeSubject={faultLikeSubject}
        />
      </ReportContentWrapper>
    );
  },
  ({ route }) => ({
    headerShown: false,
    title: route.params.faultLikeSubject
      ? `${route.params.faultLikeSubject.title}-检测结果`
      : '检测报告',
  }),
);
